import I18n from "i18n-js";
import pathToRegexp from "path-to-regexp";
import React from "react";
import { func, shape } from "prop-types";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { getLang } from "../../common/utils/langUtils";
import { alphabetically } from "../../common/utils/filterUtils";
import {
  deleteScale,
  getScalesList,
} from "../actions/scalesActions";
import List from "../../common/components/layout/List";
import Table from "../../common/components/elements/Table";
import TextInput from "../../common/components/form/TextInput";
import Select from "../../common/components/form/Select";
import DateInput from "../../common/components/form/DateInput";
import ClientUtils from "../../company/utils/ClientUtils";
import { getClientsList } from "../../company/actions/clientsActions";
import {
  ROUTE_SCALE,
  ROUTE_SCALE_UPDATE,
  ROUTE_SCALES_ADD,
} from "../../../routes";

class ScalesList extends List {
  static propTypes = {
    scales: shape().isRequired,
    clients: shape().isRequired,
    getScalesList: func.isRequired,
    getClientsList: func.isRequired,
    deleteScale: func.isRequired,
    location: shape().isRequired,
  };

  constructor() {
    super();

    this.scaleRoute = pathToRegexp.compile(ROUTE_SCALE);
    this.scaleUpdateRoute = pathToRegexp.compile(
      ROUTE_SCALE_UPDATE,
    );

    this.dataTableRow = this.dataTableRow.bind(this);
    this.onDelete = this.onDelete.bind(this);
  }

  /**
   * {@inheritdoc}
   */
  componentDidMount() {
    super.componentDidMount();

    this.props.getScalesList();
    this.props.getClientsList();
  }

  onDelete(uuid) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(I18n.t("pages.scales.confirm.delete"))) {
      return;
    }

    this.props.deleteScale(uuid);
  }

  /**
   * @return {Array}
   */
  dataTableHead() {
    return [
      I18n.t("pages.scales.head.client"),
      I18n.t("pages.scales.head.serial_number"),
      I18n.t("pages.scales.head.mark"),
      I18n.t("pages.scales.head.barcode"),
      I18n.t("pages.scales.head.last_inspection"),
      I18n.t("pages.scales.head.validity"),
      I18n.t("pages.scales.head.designation"),
      I18n.t("pages.scales.head.actions"),
    ];
  }

  /**
   * @param {Object} rowData
   *
   * @return {Array}
   */
  dataTableRow(rowData) {
    const lastInspection = new Date(
      rowData.lastInspectionDate,
    ).toLocaleDateString(getLang());
    const expirationDate = new Date(rowData.expirationDate).toLocaleDateString(
      getLang(),
    );
    const currentDate = new Date();
    const owner =
      this.props.clients.list.find(
        (client) => client.uuid === rowData.clientUuid,
      ) || null;
    const expired = currentDate > new Date(rowData.expirationDate);
    const expiration = (
      <div className={`text-${expired ? "danger" : "success"}`}>
        <i
          className={`fa-solid fa-${expired ? "times" : "check"}`}
          aria-hidden="true"
          title={expirationDate}
        />
      </div>
    );

    const actions = (
      <div>
        <Link
          to={this.scaleRoute({ uuid: rowData.uuid })}
          className="action"
        >
          <i className="fa-solid fa-eye" aria-hidden="true" />
        </Link>
        <Link
          to={this.scaleUpdateRoute({ uuid: rowData.uuid })}
          className="action"
        >
          <i className="fa-solid fa-pencil" aria-hidden="true" />
        </Link>
        <button
          onClick={() => this.onDelete(rowData.uuid)}
          className="action danger"
        >
          <i className="fa-solid fa-trash" aria-hidden="true" />
        </button>
      </div>
    );

    return [
      owner ? ClientUtils.renderLabel(owner) : I18n.t("common.unknown"),
      rowData.serialNumber,
      rowData.mark,
      rowData.barcode,
      lastInspection,
      expiration,
      rowData.designation,
      actions,
    ];
  }

  /**
   * {@inheritdoc}
   */
  renderFilters() {
    const { clients } = this.props;
    const { filters } = this.state;

    return [
      <Select
        key="filterByClient"
        id="filterByClient"
        defaultValue={filters.client}
        onChange={(event) => this.onChangeFilter(event, "client")}
        label={I18n.t("filters.by_client")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {ClientUtils.renderLabel(option)}
          </option>
        )}
        options={clients.list.sort((a, b) =>
          alphabetically(
            ClientUtils.renderLabel(a),
            ClientUtils.renderLabel(b),
          ),
        )}
        placeholder={I18n.t("filters.by_client:default")}
        optional
      />,
      <TextInput
        key="filterBySerialNumber"
        id="filterBySerialNumber"
        defaultValue={filters.serialNumber}
        onChange={(event) => this.onChangeFilter(event, "serialNumber")}
        label={I18n.t("filters.by_serial_number")}
        optional
      />,
      <TextInput
        key="filterByMark"
        id="filterByMark"
        defaultValue={filters.mark}
        onChange={(event) => this.onChangeFilter(event, "mark")}
        label={I18n.t("filters.by_mark")}
        optional
      />,
      <TextInput
        key="filterByBarcode"
        id="filterByBarcode"
        defaultValue={filters.barcode}
        onChange={(event) => this.onChangeFilter(event, "barcode")}
        label={I18n.t("filters.by_barcode")}
        optional
      />,
      <DateInput
        key="filterByStartDate"
        id="filterByStartDate"
        defaultValue={filters.startDate && new Date(Number(filters.startDate))}
        onChange={(event) => this.onChangeDateFilter(event, "startDate")}
        label={I18n.t("filters.by_start_date")}
        legend
        optional
        isClearable
      />,
      <DateInput
        key="filterByEndDate"
        id="filterByEndDate"
        defaultValue={filters.endDate && new Date(Number(filters.endDate))}
        onChange={(event) => this.onChangeDateFilter(event, "endDate")}
        label={I18n.t("filters.by_end_date")}
        legend
        optional
        isClearable
      />,
    ];
  }

  /**
   * {@inheritdoc}
   */
  renderContent() {
    const { scales, clients } = this.props;

    if (clients.inProgress || scales.inProgress) {
      return <div className="loader" />;
    }

    if (clients.error || scales.error) {
      return <p>{I18n.t("common.loadingApiError")}</p>;
    }

    const actions = (
      <Link to={ROUTE_SCALES_ADD} className="btn btn-primary">
        {I18n.t("pages.scales.list.actions.add")}
      </Link>
    );

    const list = scales.list
      .filter(this.getFilterBySelect("client"))
      .filter(this.getFilterByText("serialNumber"))
      .filter(this.getFilterByText("barcode"))
      .filter(this.getFilterByText("mark"))
      .filter(this.getFilterByDate("lastInspectionDate"))
      .sort((a, b) => {
        const clientA = clients.list.find(
          (client) => client.uuid === a.clientUuid,
        );
        const clientB = clients.list.find(
          (client) => client.uuid === b.clientUuid,
        );

        if (!clientA || !clientB) {
          return alphabetically(a.serialNumber, b.serialNumber);
        }

        return (
          alphabetically(clientA.legalCompanyName, clientB.legalCompanyName) ||
          alphabetically(a.serialNumber, b.serialNumber)
        );
      });

    return (
      <Table
        emptyContent={I18n.t("pages.scales.list.empty")}
        data={list}
        headData={this.dataTableHead()}
        rowData={this.dataTableRow}
        filters={this.renderFilters()}
        pagination
        onPageChanged={this.onPageChanged}
        initialPage={this.getCurrentPage()}
        actions={actions}
      />
    );
  }

  /**
   * {@inheritdoc}
   */
  renderTitle() {
    return I18n.t("pages.scales.list.title");
  }
}

const mapStateToProps = (state) => ({
  scales: state.scales,
  clients: state.clients,
});

const mapDispatchToProps = (dispatch) => ({
  getScalesList: () => dispatch(getScalesList()),
  getClientsList: () => dispatch(getClientsList()),
  deleteScale: (uuid) => dispatch(deleteScale(uuid)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ScalesList);
